import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Link,
  Pagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { authenticatedFetch } from '../../utils/api';
import { usePermissions } from '../../utils/permissions';
import { formatInteger } from '../../utils/formatters';
import { useTranslation } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ReceivedNoteDetail {
  id: number;
  counts: number;
  number_of_bags: number;
  weight: number;
  mill?: string;
  color?: string;
}

interface ReceivedNote {
  id: number;
  company_id: number;
  company: {
    id: number;
    name: string;
  };
  received_note_details: ReceivedNoteDetail[];
  representative_name?: string;
  representative_phone?: string;
  created_at: string;
  total_weight: number;
  total_bags: number;
  counts_display: string;
  mills_display: string;
  colors_display: string;
}

export default function ReceivedNoteList() {
  const [received_notes, setReceivedNotes] = useState<ReceivedNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { canCreate } = usePermissions();

  useEffect(() => {
    fetchReceivedNotes();
  }, [page]);

  const fetchReceivedNotes = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${API_URL}/received_notes?page=${page}&per_page=25`);
      if (!response.ok) {
        throw new Error('Failed to fetch received_notes');
      }

      const data = await response.json();
      setReceivedNotes(data);
      
      // Get pagination info from headers
      const totalPagesHeader = response.headers.get('X-Total-Pages');
      if (totalPagesHeader) {
        setTotalPages(parseInt(totalPagesHeader));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {canCreate('received_notes') && (
            <Box sx={{ 
              mb: { xs: 2, sm: 3 }, 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
            }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => navigate('/received-notes/new')}
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
              >
                {t('receivedNotes.createNew')}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isMobile ? (
            // Mobile Card View
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {received_notes.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {t('receivedNotes.noReceivedNotes')}
                </Typography>
              ) : (
                received_notes.map((received_note) => (
                  <Card 
                    key={received_note.id}
                    onClick={() => navigate(`/received-notes/${received_note.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" component="div">
                          Received Note #{received_note.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(received_note.created_at).toLocaleDateString()}
                        </Typography>
                      </Stack>
                      
                      <Link
                        component="button"
                        variant="body1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/companies/${received_note.company?.id}`);
                        }}
                        sx={{ 
                          color: 'primary.main', 
                          fontWeight: 500, 
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          display: 'block',
                          textAlign: 'left',
                          mb: 1,
                        }}
                      >
                        {received_note.company?.name}
                      </Link>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Counts:</Typography>
                          <Typography variant="body2" fontWeight={500}>{received_note.counts_display}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Total Bags:</Typography>
                          <Typography variant="body2" fontWeight={500}>{formatInteger(received_note.total_bags)}</Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Total Weight:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {typeof received_note.total_weight === 'number' ? received_note.total_weight.toFixed(2) : Number(received_note.total_weight || 0).toFixed(2)} kg
                          </Typography>
                        </Box>
                        
                        {received_note.colors_display && received_note.colors_display !== '-' && (
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">Colors:</Typography>
                            <Typography variant="body2" fontWeight={500}>{received_note.colors_display}</Typography>
                          </Box>
                        )}
                        
                        {received_note.mills_display && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Mills:</Typography>
                            <Typography variant="body2">{received_note.mills_display}</Typography>
                          </Box>
                        )}
                        
                        {received_note.representative_name && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Representative:</Typography>
                            <Typography variant="body2">{received_note.representative_name}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Received Note ID</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Colors</TableCell>
                    <TableCell>Counts</TableCell>
                    <TableCell>Total Bags</TableCell>
                    <TableCell>Total Weight (kg)</TableCell>
                    <TableCell>Mills</TableCell>
                    <TableCell>Representative</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {received_notes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        {t('receivedNotes.noReceivedNotes')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    received_notes.map((received_note) => (
                      <TableRow 
                        key={received_note.id}
                        onClick={() => navigate(`/received-notes/${received_note.id}`)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                      >
                        <TableCell>{received_note.id}</TableCell>
                        <TableCell>
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/companies/${received_note.company?.id}`);
                            }}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {received_note.company?.name}
                          </Link>
                        </TableCell>
                        <TableCell>{received_note.colors_display}</TableCell>
                        <TableCell>{received_note.counts_display}</TableCell>
                        <TableCell>{formatInteger(received_note.total_bags)}</TableCell>
                        <TableCell>{typeof received_note.total_weight === 'number' ? received_note.total_weight.toFixed(2) : Number(received_note.total_weight || 0).toFixed(2)}</TableCell>
                        <TableCell>{received_note.mills_display}</TableCell>
                        <TableCell>{received_note.representative_name || '-'}</TableCell>
                        <TableCell>{new Date(received_note.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

